import React from "react";
import EntityTable from "../EntityTable";
import {
  anchorConnectionColumnId,
  renderRowUpdateAction,
} from "../../common/TableUtils";
import { ConnectionFormDataController } from "../../controllers/formController";
import useVersionTeamQuery from "../../hooks/query/connections/useVersionTeamQuery";
import { Version } from "../../types/version";
import { Team } from "../../types/team";
import {
  SystemTeamContributionTypes,
  VersionTeamConnection,
} from "../../types/versionTeam";
import VersionTeamDialog from "../modals/VersionTeam/VersionTeamDialog";
import useVersionTeamConnectionLogic from "../../hooks/logic/connections/versionTeamConnectionLogic";
import useServerFilters from "../../hooks/useServerFilters";
import { versionTeamFilterSpec } from "../../common/entities/VersionTeamColumns";

interface VersionTeamTableProps {
  controller: ConnectionFormDataController<
    VersionTeamConnection,
    Version,
    Team
  >;
  versionTeamType: SystemTeamContributionTypes;
  systemLanguageType: VersionTeamConnection["system_language_type"];
}

const VersionTeamTable: React.FC<VersionTeamTableProps> = ({
  controller,
  versionTeamType,
  systemLanguageType,
}) => {
  const {
    serverFilters,
    entityTableFilterProps,
    pagination,
    setPagination,
  } = useServerFilters<VersionTeamConnection>({}, versionTeamFilterSpec);
  const { useFetchConnectedEdges } = useVersionTeamQuery(
    versionTeamType,
    systemLanguageType
  );
  const { anchorEntity, direction, openEditDialog } = controller;

  const { data, isLoading, errorMessage } = useFetchConnectedEdges(
    anchorEntity._id,
    direction,
    {
      bodyParams: serverFilters,
      externalPagination: pagination,
      externalSetPagination: setPagination,
    }
  );

  const { languageBasedColumns } = useVersionTeamConnectionLogic(
    controller.from,
    controller.to,
    versionTeamType,
    systemLanguageType
  );

  const renderRowActions = renderRowUpdateAction<VersionTeamConnection>((row) =>
    openEditDialog(row._key)
  );

  return (
    <>
      <EntityTable
        loading={isLoading}
        error={errorMessage}
        columns={languageBasedColumns}
        hiddenColumnIds={[anchorConnectionColumnId(direction)]}
        renderRowActions={renderRowActions}
        enableRowActions
        data={data?.data || []}
        pagination={pagination}
        manualPagination
        onPaginationChange={setPagination}
        totalRowCount={data?.total || 0}
        manualFiltering
        {...entityTableFilterProps}
      />
      <VersionTeamDialog
        controller={controller}
        versionTeamType={versionTeamType}
        systemLanguageType={systemLanguageType}
      />
    </>
  );
};

export default VersionTeamTable;
